/**
 * Pure, side-effect-free helpers for the table's Excel-style filtering.
 *
 * Two filter modes are layered on top of the existing "condition" filtering
 * (StringFilterTypes / NumberFilterTypes / …) without touching it:
 *   - "value": Excel-style "Filter by Value" — checkbox list of unique values.
 *   - "color": Excel-style "Filter by Color" — by the cell background color
 *     produced by the column's `calculatedCellBgColor` function.
 *
 * Everything here is deterministic and unit-tested in
 * `table-filter-helpers.test.ts`. The component (base-table.tsx) only
 * dispatches into these functions; it owns no filtering logic of its own
 * for the value/color modes.
 */

/** Sentinel stored in `selectedValues` to represent null/undefined/empty cells. */
export const BLANK_FILTER_KEY = "__KUIKA_FILTER_BLANK__";

/** Discriminator + payload for a single column's persisted filter. */
export interface TableColumnFilter {
  /** Defaults to "condition" when absent (backward-compatible with old filters). */
  filterMode?: "condition" | "value" | "color";
  columnId?: string;
  /** For value/color modes this is always "AND" (OR lives inside the selection lists). */
  logicalOperator?: "AND" | "OR";

  // --- condition mode (existing fields, untouched) ---
  // Runtime value can be string (text/date), number (InputNumber) or boolean (TriStateSwitch).
  value?: string | number | boolean;
  value2?: string | number | boolean;
  type?: string;
  secondType?: string;
  secondValue?: string | number | boolean;
  secondValue2?: string | number | boolean;
  dateSearchFormat?: string;

  // --- value mode ---
  /** Stable keys (String(rawValue) or BLANK_FILTER_KEY); matched with OR semantics. */
  selectedValues?: string[];

  // --- color mode (background only) ---
  /** Reserved for future font-color support; today always "background". */
  colorTarget?: "background";
  /** Normalized color strings; matched with OR semantics. */
  selectedColors?: string[];
}

/** One entry in the "Filter by Value" checkbox list. */
export interface UniqueFilterValue {
  /** Stable identity/dedup key — what gets stored in `selectedValues`. */
  key: string;
  /** Human-readable label shown in the checkbox list. */
  label: string;
}

/** Parsed color channels (0-255 for rgb, 0-1 for alpha). */
interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Resolves a cell's background color from its row data, mirroring render/export. */
export type CellBgColorFn = (rowIndex?: number, rowData?: unknown) => string | undefined;

/** Minimal indexable view of a table row. Rows are genuinely dynamic (no fixed shape). */
interface KeyedRecord {
  [key: string]: unknown;
}

/** Reads a field off a row by key without leaking `any` to callers. */
export const readRowField = (row: unknown, key: string): unknown => {
  if (row && typeof row === "object" && key) {
    return (row as KeyedRecord)[key];
  }
  return undefined;
};

/** True for values that should bucket under "(Blank)". */
const isBlankValue = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === "string" && value.trim() === "");

const defaultLabel = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/**
 * Stable dedup/identity key for a non-blank cell value. Must be used by BOTH the
 * unique-value list and the per-row matcher so the selection a user makes maps
 * back to the same rows. Objects serialize to JSON (so structurally different
 * objects stay distinct) rather than collapsing to "[object Object]".
 */
export const valueKey = (rawValue: unknown): string => {
  if (typeof rawValue === "object" && rawValue !== null) {
    try {
      return JSON.stringify(rawValue);
    } catch {
      return String(rawValue);
    }
  }
  return String(rawValue);
};

const blankLabelFor = (language: string | null | undefined): string => (language === "tr_TR" ? "(Boş)" : "(Blank)");

/**
 * Builds the deduplicated, sorted list of unique values for a column.
 * Dedup key is the EXACT string form of the raw value (culture-invariant —
 * Excel treats "İstanbul" and "istanbul" as distinct; do NOT fold case here).
 */
export const getUniqueColumnValues = (
  dataSource: readonly unknown[] | undefined,
  dataValue: string,
  language: string | null,
  formatLabel?: (rawValue: unknown) => string
): UniqueFilterValue[] => {
  const seen: Set<string> = new Set<string>();
  const result: UniqueFilterValue[] = [];
  let hasBlank: boolean = false;

  for (const row of dataSource ?? []) {
    const raw: unknown = readRowField(row, dataValue);
    if (isBlankValue(raw)) {
      hasBlank = true;
      continue;
    }
    const key: string = valueKey(raw);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    const label: string = formatLabel ? formatLabel(raw) : defaultLabel(raw);
    result.push({ key, label });
  }

  const collator: string = language === "tr_TR" ? "tr" : "en";
  result.sort((a: UniqueFilterValue, b: UniqueFilterValue): number =>
    a.label.localeCompare(b.label, collator, { numeric: true, sensitivity: "base" })
  );

  if (hasBlank) {
    result.unshift({ key: BLANK_FILTER_KEY, label: blankLabelFor(language) });
  }

  return result;
};

/**
 * True when the row's value passes a "Filter by Value" selection.
 * An empty selection means the filter is inactive → everything passes.
 */
export const matchesValueFilter = (rawValue: unknown, selectedValues: readonly string[] | undefined): boolean => {
  if (!selectedValues || selectedValues.length === 0) {
    return true;
  }
  const key: string = isBlankValue(rawValue) ? BLANK_FILTER_KEY : valueKey(rawValue);
  return selectedValues.includes(key);
};

/**
 * Common CSS named colors → hex. So a column whose `calculatedCellBgColor`
 * returns e.g. "red" for some rows and "#ff0000" for others buckets them as one
 * color instead of two. Not the full 148-name CSS set — just the ones realistic
 * for conditional formatting; unknown names still fall back to the literal string.
 */
const NAMED_COLOR_HEX: Record<string, string> = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  lime: "#00ff00",
  blue: "#0000ff",
  yellow: "#ffff00",
  orange: "#ffa500",
  purple: "#800080",
  gray: "#808080",
  grey: "#808080",
  silver: "#c0c0c0",
  maroon: "#800000",
  olive: "#808000",
  navy: "#000080",
  teal: "#008080",
  aqua: "#00ffff",
  cyan: "#00ffff",
  fuchsia: "#ff00ff",
  magenta: "#ff00ff",
  pink: "#ffc0cb"
};

/**
 * Parses a CSS color (hex #rgb/#rgba/#rrggbb/#rrggbbaa, rgb()/rgba(), or a
 * common named color) to channels. Returns null for anything it can't parse
 * (uncommon named colors, gradients, etc.).
 */
export const parseColorToRgb = (color: string | null | undefined): RgbaColor | null => {
  if (!color) {
    return null;
  }
  let c: string = color.trim().toLowerCase();

  const named: string | undefined = NAMED_COLOR_HEX[c];
  if (named) {
    c = named;
  }

  const rgbaMatch: RegExpMatchArray | null = c.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/
  );
  if (rgbaMatch) {
    return {
      r: Math.round(Number(rgbaMatch[1])),
      g: Math.round(Number(rgbaMatch[2])),
      b: Math.round(Number(rgbaMatch[3])),
      a: rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1
    };
  }

  let hex: string = c.startsWith("#") ? c.slice(1) : c;
  if (/^[0-9a-f]{3,4}$/.test(hex)) {
    hex = hex
      .split("")
      .map((ch: string): string => ch + ch)
      .join("");
  }
  if (/^[0-9a-f]{6}$/.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: 1
    };
  }
  if (/^[0-9a-f]{8}$/.test(hex)) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: parseInt(hex.slice(6, 8), 16) / 255
    };
  }

  return null;
};

/**
 * Canonicalizes a color so the same color in different notations
 * (#fff / #ffffff / rgb(255,255,255)) compares and dedups as one.
 * Unparseable colors fall back to their trimmed/lowercased string.
 */
export const normalizeColor = (color: string | null | undefined): string => {
  if (!color) {
    return "";
  }
  const trimmed: string = color.trim().toLowerCase();
  if (trimmed === "transparent") {
    return "transparent";
  }
  const rgb: RgbaColor | null = parseColorToRgb(trimmed);
  if (!rgb) {
    return trimmed;
  }
  const alpha: number = Math.round(rgb.a * 100) / 100;
  return alpha >= 1 ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

/**
 * Builds the deduplicated list of background colors actually used in a column,
 * by re-running the column's `calculatedCellBgColor` per row (same source the
 * cell renderer and Excel export use). Empty / transparent cells are skipped.
 */
export const getUniqueCellBgColors = (
  dataSource: readonly unknown[] | undefined,
  colorFn: CellBgColorFn | undefined
): string[] => {
  if (!colorFn) {
    return [];
  }
  const seen: Set<string> = new Set<string>();
  for (const row of dataSource ?? []) {
    const raw: string | undefined = colorFn(undefined, row);
    const norm: string = normalizeColor(raw);
    if (norm && norm !== "transparent") {
      seen.add(norm);
    }
  }
  return Array.from(seen);
};

/**
 * True when the cell's background color passes a "Filter by Color" selection.
 * An empty selection means the filter is inactive → everything passes.
 */
export const matchesColorFilter = (
  cellColor: string | null | undefined,
  selectedColors: readonly string[] | undefined
): boolean => {
  if (!selectedColors || selectedColors.length === 0) {
    return true;
  }
  const norm: string = normalizeColor(cellColor ?? "");
  if (!norm) {
    return false;
  }
  return selectedColors.includes(norm);
};
